import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import DisplayCard from '@/components/displays/DisplayCard';
import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { Favoritable } from '@/types';

const Favorites = () => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'sequences' | 'displays'>('sequences');
  const getFavoritesByType = useFavoritesStore(state => state.getFavoritesByType);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  const sequenceFavorites = getFavoritesByType('sequence');
  const displayFavorites = getFavoritesByType('display');

  const renderEmptyState = (type: 'sequences' | 'displays') => (
    <div className="text-center py-20 bg-muted/50 rounded-lg">
      <Heart className="mx-auto text-muted-foreground" size={48} />
      <h3 className="mt-4 text-xl font-medium">No favorite {type} yet</h3>
      <p className="mt-2 text-muted-foreground">
        {type === 'sequences' 
          ? 'Save your favorite sequences to find them easily later'
          : 'Save your favorite displays to keep track of them'}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="grow pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-6 gap-3">
            <Heart className="text-primary" size={24} />
            <h1 className="text-3xl font-bold">Your Favorites</h1>
          </div>
          
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'sequences' | 'displays')} className="mb-8">
            <TabsList className="mb-8">
              <TabsTrigger 
                value="sequences" 
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Sequences
              </TabsTrigger>
              <TabsTrigger 
                value="displays"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Displays
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="sequences" className="mt-0">
              {sequenceFavorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {sequenceFavorites.map(favorite => (
                    <SequenceCard 
                      key={favorite.id} 
                      sequence={{
                        ...favorite,
                        type: 'sequence'
                      }}
                    />
                  ))}
                </div>
              ) : renderEmptyState('sequences')}
            </TabsContent>

            <TabsContent value="displays" className="mt-0">
              {displayFavorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {displayFavorites.map(favorite => (
                    <DisplayCard 
                      key={favorite.id} 
                      display={{
                        ...favorite,
                        type: 'display'
                      }}
                    />
                  ))}
                </div>
              ) : renderEmptyState('displays')}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Favorites;
